import moment from 'moment';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createDebouncedJSONStorage } from 'zustand-debounce';
import indexedDbStorage from '../utils/indexeddb-zustand-store';
import { ValidationTabsEnum } from '../types/validation';
import { IAnalytcisUserInput } from '../types/IAnalyticsUserInput';
import { analyticsUserInput } from '../utils/analyticsUserInput';

export const storageName = 'MOOD_analytics_selection_store';

export type MarkerAnalyticsSelection = {
  activePlotAnalytics: string[];
  isAdvanceAnalytics: boolean;
  activeValidationTab: ValidationTabsEnum;
  isForAllAnalytics: boolean;
  activeInsituTab: number | null;
  insituVariable: string | number;
  insituStartDate: moment.Moment | null;
  insituEndDate: moment.Moment | null;
};

export const DEFAULT_MARKER_SELECTION: MarkerAnalyticsSelection = {
  activePlotAnalytics: [],
  isAdvanceAnalytics: false,
  activeValidationTab: ValidationTabsEnum.SATELLITE_TAB,
  isForAllAnalytics: false,
  activeInsituTab: null,
  insituVariable: '',
  insituStartDate: null,
  insituEndDate: null,
};

export const DEFAULT_ACTIVE_PLOT_VALIDATION = ['Altimeter Comparison'];

type AnalyticsSelectionStore = {
  markerSelections: Record<string, MarkerAnalyticsSelection>;
  markerUserInputs: Record<string, IAnalytcisUserInput>;
  activePlotValidation: string[];

  getMarkerSelection: (markerId: string) => MarkerAnalyticsSelection;
  setActivePlotAnalytics: (markerId: string, value: string[]) => void;
  setIsAdvanceAnalytics: (markerId: string, value: boolean) => void;
  setActiveValidationTab: (
    markerId: string,
    value: ValidationTabsEnum
  ) => void;
  setIsForAllAnalytics: (markerId: string, value: boolean) => void;
  setActivePlotValidation: (value: string[]) => void;
  setActiveInsituTab: (markerId: string, value: number) => void;
  setInsituVariable: (markerId: string, value: string | number) => void;
  setInsituStartDate: (markerId: string, value: moment.Moment | null) => void;
  setInsituEndDate: (markerId: string, value: moment.Moment | null) => void;

  getUserInput: (markerId: string) => IAnalytcisUserInput;
  setUserInput: (
    markerId: string,
    update: Partial<IAnalytcisUserInput>
  ) => void;

  resetMarkerSelection: (markerId: string) => void;
  reset: () => void;
};

const updateMarkerSelection = (
  markerSelections: Record<string, MarkerAnalyticsSelection>,
  markerId: string,
  update: Partial<MarkerAnalyticsSelection>
) => ({
  ...markerSelections,
  [markerId]: {
    ...DEFAULT_MARKER_SELECTION,
    ...markerSelections[markerId],
    ...update,
  },
});

const reviveMomentDates = (
  userInput: IAnalytcisUserInput
): IAnalytcisUserInput => {
  const revived = {} as Record<string, unknown>;
  for (const [plotType, params] of Object.entries(userInput)) {
    const typedParams = params as Record<string, unknown>;
    revived[plotType] = {
      ...typedParams,
      startDate: typedParams.startDate ? moment(typedParams.startDate as any) : typedParams.startDate,
      endDate: typedParams.endDate ? moment(typedParams.endDate as any) : typedParams.endDate,
    };
  }
  return revived as unknown as IAnalytcisUserInput;
};

export const useAnalyticsSelectionStore = create<AnalyticsSelectionStore>()(
  persist(
    (set, get) => ({
      markerSelections: {},
      markerUserInputs: {},
      activePlotValidation: DEFAULT_ACTIVE_PLOT_VALIDATION,

      getMarkerSelection: (markerId) =>
        get().markerSelections[markerId] || DEFAULT_MARKER_SELECTION,

      setActivePlotAnalytics: (markerId, value) =>
        set((state) => ({
          markerSelections: updateMarkerSelection(
            state.markerSelections,
            markerId,
            { activePlotAnalytics: value }
          ),
        })),

      setIsAdvanceAnalytics: (markerId, value) =>
        set((state) => ({
          markerSelections: updateMarkerSelection(
            state.markerSelections,
            markerId,
            { isAdvanceAnalytics: value }
          ),
        })),

      setActiveValidationTab: (markerId, value) =>
        set((state) => ({
          markerSelections: updateMarkerSelection(
            state.markerSelections,
            markerId,
            { activeValidationTab: value }
          ),
        })),

      setIsForAllAnalytics: (markerId, value) =>
        set((state) => ({
          markerSelections: updateMarkerSelection(
            state.markerSelections,
            markerId,
            { isForAllAnalytics: value }
          ),
        })),

      setActivePlotValidation: (value) => set({ activePlotValidation: value }),

      setActiveInsituTab: (markerId, value) =>
        set((state) => ({
          markerSelections: updateMarkerSelection(
            state.markerSelections,
            markerId,
            { activeInsituTab: value }
          ),
        })),

      setInsituVariable: (markerId, value) =>
        set((state) => ({
          markerSelections: updateMarkerSelection(
            state.markerSelections,
            markerId,
            { insituVariable: value }
          ),
        })),

      setInsituStartDate: (markerId, value) =>
        set((state) => ({
          markerSelections: updateMarkerSelection(
            state.markerSelections,
            markerId,
            { insituStartDate: value }
          ),
        })),

      setInsituEndDate: (markerId, value) =>
        set((state) => ({
          markerSelections: updateMarkerSelection(
            state.markerSelections,
            markerId,
            { insituEndDate: value }
          ),
        })),

      getUserInput: (markerId) =>
        get().markerUserInputs[markerId] || analyticsUserInput,

      setUserInput: (markerId, update) =>
        set((state) => ({
          markerUserInputs: {
            ...state.markerUserInputs,
            [markerId]: {
              ...analyticsUserInput,
              ...state.markerUserInputs[markerId],
              ...update,
            },
          },
        })),

      resetMarkerSelection: (markerId) =>
        set((state) => {
          const markerSelections = { ...state.markerSelections };
          const markerUserInputs = { ...state.markerUserInputs };
          delete markerSelections[markerId];
          delete markerUserInputs[markerId];
          return { markerSelections, markerUserInputs };
        }),

      reset: () =>
        set({
          markerSelections: {},
          markerUserInputs: {},
          activePlotValidation: DEFAULT_ACTIVE_PLOT_VALIDATION,
        }),
    }),
    {
      name: storageName,
      storage: createDebouncedJSONStorage(indexedDbStorage, {
        debounceTime: 500,
      }),
      version: 2,
      partialize: (state) => ({
        markerSelections: state.markerSelections,
        markerUserInputs: state.markerUserInputs,
        activePlotValidation: state.activePlotValidation,
      }),
      merge: (persistedState, currentState) => {
        const persisted = (persistedState as Partial<AnalyticsSelectionStore>) || {};
        const persistedMarkerSelections = persisted.markerSelections || {};
        const revivedMarkerSelections: Record<string, MarkerAnalyticsSelection> = {};
        for (const [markerId, selection] of Object.entries(
          persistedMarkerSelections
        )) {
          revivedMarkerSelections[markerId] = {
            ...DEFAULT_MARKER_SELECTION,
            ...selection,
            insituStartDate: selection.insituStartDate
              ? moment(selection.insituStartDate as any)
              : null,
            insituEndDate: selection.insituEndDate
              ? moment(selection.insituEndDate as any)
              : null,
          };
        }

        const persistedMarkerUserInputs = persisted.markerUserInputs || {};
        const revivedMarkerUserInputs: Record<string, IAnalytcisUserInput> =
          {};
        for (const [markerId, userInput] of Object.entries(
          persistedMarkerUserInputs
        )) {
          revivedMarkerUserInputs[markerId] = reviveMomentDates(userInput);
        }

        return {
          ...currentState,
          ...persisted,
          markerSelections: revivedMarkerSelections,
          markerUserInputs: revivedMarkerUserInputs,
        };
      },
    }
  )
);

export const isStoreEmpty = (state: AnalyticsSelectionStore): boolean => {
  return (
    Object.keys(state.markerSelections).length === 0 &&
    Object.keys(state.markerUserInputs).length === 0 &&
    state.activePlotValidation.length ===
      DEFAULT_ACTIVE_PLOT_VALIDATION.length &&
    state.activePlotValidation.every(
      (item, index) => item === DEFAULT_ACTIVE_PLOT_VALIDATION[index]
    )
  );
};
